import { defineStore } from "pinia";
import { useUserStore } from "./userStore";
import Api from "src/utils/api";

export const useRxDBStore = defineStore("rxdb_store", {
  persist: true,
  state: () => ({
    collectionSyncStatus: {},
    allCollectionsSynced: false,
    allColelctionAdded: false,
    syncedAt: null,
    firstSyncCompleted: false,
    // Schema cache for optimization
    schemaCache: {
      data: null,
      lastFetched: null
    },
    localSyncQueue: {},
    localSyncProgress: 100
  }),
  getters: {
    getCollectionSyncStatus: (state) => state.collectionSyncStatus,
    isAllCollectionsSynced: (state) => state.allCollectionsSynced,
    isAllCollectionsAdded: (state) => state.allColelctionAdded,
    getSyncedAt: (state) => state.syncedAt,
    // Check if this is the first sync (show dialog) or subsequent sync (background)
    isFirstSyncCompleted: (state) => state.firstSyncCompleted,
    // Check if schema cache is valid (not older than 1 day)
    isSchemaCacheValid: (state) => {
      if (!state.schemaCache.data || !state.schemaCache.lastFetched) {
        return false;
      }
      const oneDayInMs = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
      const now = new Date().getTime();
      const lastFetched = new Date(state.schemaCache.lastFetched).getTime();
      return now - lastFetched < oneDayInMs;
    },

    getLocalSyncQueue: (state) => state.localSyncQueue,
    getLocalSyncQueueStatus: (state) => {
      const result = {};

      // Process each table in the localSyncQueue
      Object.keys(state.localSyncQueue).forEach((tableName) => {
        const tableData = state.localSyncQueue[tableName];
        const tableCount = Object.keys(tableData).length;
        const tableSyncedCount = Object.values(tableData).filter(
          (isSynced) => isSynced
        ).length;
        const tableNotSyncedCount = tableCount - tableSyncedCount;

        result[tableName] = {
          total: tableCount,
          synced: tableSyncedCount,
          notSynced: tableNotSyncedCount,
          processedPercentage: ((tableSyncedCount / tableCount) * 100).toFixed(
            2
          )
        };
      });

      // Also provide overall summary
      const allTables = Object.values(result);
      const totalItems = allTables.reduce((sum, table) => sum + table.total, 0);
      const totalSynced = allTables.reduce(
        (sum, table) => sum + table.synced,
        0
      );
      const totalNotSynced = allTables.reduce(
        (sum, table) => sum + table.notSynced,
        0
      );

      result.overall = {
        total: totalItems,
        synced: totalSynced,
        notSynced: totalNotSynced,
        processedPercentage: ((totalSynced / totalItems) * 100).toFixed(2)
      };
      state.localSyncProgress = result.overall.processedPercentage;

      return result;
    },
    getLocalSyncProgress: (state) => state.localSyncProgress
  },
  actions: {
    updateLocalSyncQueue(tableName, id, isSynced = false) {
      // if table name is not in localSyncQueue, create an empty object for it
      if (!this.localSyncQueue[tableName]) {
        this.localSyncQueue[tableName] = {};
      }
      // if id is not in localSyncQueue[tableName], create an empty object for it
      if (!this.localSyncQueue[tableName][id]) {
        this.localSyncQueue[tableName][id] = isSynced;
      }
      this.localSyncQueue[tableName][id] = isSynced;
    },
    resetCollectionSyncStatus() {
      const userStore = useUserStore();
      if (
        userStore.isOnline &&
        Object.keys(this.collectionSyncStatus).length == 0
      ) {
        // only reset if user is online and no collections are synced
        this.collectionSyncStatus = {};
        this.allCollectionsSynced = false;
        this.syncedAt = null;
      } else if (userStore.isOnline) {
        // If online and collections exist, reset sync status
        this.collectionSyncStatus = {};
        this.allCollectionsSynced = false;
        this.syncedAt = null;
      } else {
        // When offline, preserve existing sync status
        console.log("User is offline, preserving existing sync status");
        this.allCollectionsSynced = false;
        setTimeout(() => {
          this.allCollectionsSynced = true;
        }, 10);
      }
    },

    // Fetch schema data with caching
    async fetchSchemaData() {
      // Check if we have valid cached data
      if (this.isSchemaCacheValid) {
        return this.schemaCache.data;
      }

      const _res = await Api.RxdbSchemas.getRxDBSchemas([
        {
          field: "wait_until_finish",
          value: false
        }
      ]);

      if (_res.error) {
        return null;
      }

      // Update cache
      this.schemaCache = {
        data: _res.data,
        lastFetched: new Date().toISOString()
      };

      return _res.data;
    },

    async setInitialCollection(collectionName) {
      if (!(collectionName in this.collectionSyncStatus)) {
        this.collectionSyncStatus[collectionName] = true;
      }
    },

    setAllCollectionsAdded(status) {
      this.allColelctionAdded = status;
    },

    // Mark first sync as completed (no more dialogs)
    markFirstSyncCompleted() {
      this.firstSyncCompleted = true;
      this.setAllCollectionsAdded(true);
      this.allCollectionsSynced = true;
      this.syncedAt = new Date();
      console.log("First sync completed :", this.firstSyncCompleted);
    },

    async setCollectionSyncStatus(collectionName, status) {
      let schemaData = await this.fetchSchemaData();
      if (
        !schemaData ||
        schemaData === undefined ||
        (Array.isArray(schemaData) && schemaData.length === 0)
      ) {
        schemaData = [];
      }

      // Check if collection should wait until finish
      const noNeedToWait = schemaData.some(
        (schema) =>
          schema.table_name === collectionName && schema.wait_until_finish
      );
      if (noNeedToWait) return;

      // Update status for this collection
      if (collectionName in this.collectionSyncStatus) {
        this.collectionSyncStatus[collectionName] = status;
      }

      // Check if all collections are synced
      const allSynced = !Object.values(this.collectionSyncStatus).some(Boolean);

      // Clear existing timeout
      clearTimeout(this._syncTimeout);

      // Debounce sync status update
      this._syncTimeout = setTimeout(() => {
        const userStore = useUserStore();
        if (!userStore.isOnline) return;

        this.allCollectionsSynced = allSynced;
        if (allSynced) {
          this.syncedAt = new Date();
        }
        this._syncTimeout = null;
      }, 1000);
    },

    // Method to clear schema cache (useful for testing or manual refresh)
    clearSchemaCache() {
      this.schemaCache = {
        data: null,
        lastFetched: null
      };
    }
  },
  $reset() {
    this.collectionSyncStatus = {};
    this.allCollectionsSynced = false;
    this.firstSyncCompleted = false;
    this.schemaCache = {
      data: null,
      lastFetched: null
    };
  }
});
